import { useId, useRef, type KeyboardEvent } from "react";
import { GlassLens } from "./GlassLens";

export interface GlassSwitchOption<T extends string> {
  value: T;
  label: string;
}

interface GlassSwitchProps<T extends string> {
  label: string;
  value: T;
  options: GlassSwitchOption<T>[];
  onChange: (value: T) => void;
  size?: "sm" | "md";
  /** Stretch options to the full width (default) or size them to their labels (scrollable rows). */
  fill?: boolean;
}

/**
 * Liquid-glass segmented switch (radio group): the same glass surface and
 * flowing lens as the tab bar. Arrow keys move the selection (roving tabindex).
 */
export function GlassSwitch<T extends string>({ label, value, options, onChange, size = "md", fill = true }: GlassSwitchProps<T>) {
  const group = useId();
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const onKey = (e: KeyboardEvent, i: number) => {
    const step = e.key === "ArrowRight" || e.key === "ArrowDown" ? 1 : e.key === "ArrowLeft" || e.key === "ArrowUp" ? -1 : 0;
    if (!step) return;
    e.preventDefault();
    const next = (i + step + options.length) % options.length;
    onChange(options[next]!.value);
    refs.current[next]?.focus();
  };
  return (
    <ul role="radiogroup" aria-label={label} className={`glass-switch glass-surface glass-switch-${size} ${fill ? "flex" : "inline-flex"} list-none`}>
      {options.map((o, i) => {
        const checked = o.value === value;
        return (
          <li key={o.value} className={fill ? "flex flex-1" : "flex"}>
            <button
              ref={(el) => {
                refs.current[i] = el;
              }}
              type="button"
              role="radio"
              aria-checked={checked}
              tabIndex={checked ? 0 : -1}
              onClick={() => onChange(o.value)}
              onKeyDown={(e) => onKey(e, i)}
            >
              {checked ? <GlassLens group={group} /> : null}
              {o.label}
            </button>
          </li>
        );
      })}
    </ul>
  );
}
